import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, MapPin, Calendar, Instagram, ExternalLink, Award, Loader2, Mountain, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LinkHub() {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [totalRaised, setTotalRaised] = useState(47); // Starting amount
  const goalAmount = 1000;

  const handleDonate = async (amount) => {
    setSelectedAmount(amount);
    setIsProcessing(true);

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API}/payments/v1/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount,
          donor_name: "Anonymous",
          donor_email: "",
          origin_url: originUrl,
          donation_type: "Move, Heal, Fuel Scholarship"
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      setIsProcessing(false);
    }
  };

  const progressPercentage = Math.min((totalRaised / goalAmount) * 100, 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#17181B] via-[#1a1b1f] to-[#17181B]">
      {/* Header */}
      <div className="pt-8 pb-4 px-4 text-center">
        <img 
          src="/logo.png" 
          alt="Calvin D. Butts Inc." 
          className="h-16 mx-auto mb-2 invert"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <h1 className="text-2xl font-bold text-white">Calvin D. Butts Inc.</h1>
        <p className="text-white/60 text-sm">Empowering Youth, Building Futures</p>
      </div>

      <div className="max-w-md mx-auto px-4 pb-12 space-y-4">
        {/* Event Flyer */}
        <div className="rounded-2xl overflow-hidden shadow-2xl border-2 border-[#FFE102]">
          <img 
            src="/events/move-heal-fuel.png"
            alt="Move, Heal, Fuel Event - June 13"
            className="w-full h-auto"
          />
        </div>

        {/* Fund a Scholar Campaign */}
        <Card className="bg-gradient-to-br from-[#1167B1] to-[#0d5390] border-0 shadow-xl overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 bg-[#FFE102] rounded-full flex items-center justify-center">
                <Award className="w-5 h-5 text-[#17181B]" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Fund a Scholar</h2>
                <p className="text-white/70 text-xs">20 people × $25 = 1 scholarship</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-white font-bold">${totalRaised} raised</span>
                <span className="text-white/70">${goalAmount} goal</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden">
                <div 
                  className="bg-[#FFE102] h-4 rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-2"
                  style={{ width: `${Math.max(progressPercentage, 8)}%` }}
                >
                  {progressPercentage >= 15 && (
                    <span className="text-[10px] font-bold text-[#17181B]">{Math.round(progressPercentage)}%</span>
                  )}
                </div>
              </div>
              <p className="text-white/60 text-xs mt-2 text-center">
                Each $500 scholarship changes a life
              </p>
            </div>

            {/* Quick Donate Buttons */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <Button
                onClick={() => handleDonate(10)}
                disabled={isProcessing}
                className="bg-white/10 hover:bg-[#FFE102] hover:text-[#17181B] text-white border border-white/30 font-bold py-6 text-lg transition-all"
                data-testid="donate-10"
              >
                {isProcessing && selectedAmount === 10 ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "$10"
                )}
              </Button>
              <Button
                onClick={() => handleDonate(25)}
                disabled={isProcessing}
                className="bg-[#FFE102] hover:bg-[#FFE102]/90 text-[#17181B] font-bold py-6 text-lg relative overflow-hidden"
                data-testid="donate-25"
              >
                {isProcessing && selectedAmount === 25 ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    $25
                    <span className="absolute -top-1 -right-1 bg-[#17181B] text-[#FFE102] text-[8px] px-1.5 py-0.5 rounded-full font-bold">
                      POPULAR
                    </span>
                  </>
                )}
              </Button>
              <Button
                onClick={() => handleDonate(50)}
                disabled={isProcessing}
                className="bg-white/10 hover:bg-[#FFE102] hover:text-[#17181B] text-white border border-white/30 font-bold py-6 text-lg transition-all"
                data-testid="donate-50"
              >
                {isProcessing && selectedAmount === 50 ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "$50"
                )}
              </Button>
            </div>

            <p className="text-white/50 text-xs text-center">
              Tax-deductible • 501(c)(3) • EIN: 85-4072927
            </p>
          </CardContent>
        </Card>

        {/* Apply for Scholarship Button */}
        <a 
          href="mailto:laquanda.curry@dhs.ga.gov?subject=Move%2C%20Heal%2C%20Fuel%20Scholarship%20Application&body=Hello%2C%0A%0AI%20am%20interested%20in%20applying%20for%20the%20%24500%20scholarship%20through%20Calvin%20D.%20Butts%20Inc.%0A%0AName%3A%20%0AAge%3A%20%0ASchool%3A%20%0APhone%3A%20%0A%0AThank%20you!"
          className="block"
        >
          <Button className="w-full bg-[#FFE102] hover:bg-[#FFE102]/90 text-[#17181B] font-bold py-6 text-lg shadow-lg">
            <Award className="w-5 h-5 mr-2" />
            Apply for $500 Scholarship
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </a>

        {/* Get Directions */}
        <a 
          href="https://maps.google.com/?q=4731+Jonesboro+Rd+%23230,+Union+City,+GA+30291"
          target="_blank" 
          rel="noopener noreferrer"
          className="block"
        >
          <Card className="bg-white/5 border border-white/10 hover:border-[#1167B1] transition-all cursor-pointer">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#1167B1] rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-bold">Get Directions</p>
                <p className="text-white/60 text-sm">4731 Jonesboro Rd #230, Union City, GA</p>
              </div>
              <ExternalLink className="w-5 h-5 text-white/40" />
            </CardContent>
          </Card>
        </a>

        {/* Save the Date - June 27 Hike */}
        <Card className="bg-gradient-to-r from-green-600 to-green-700 border-0 shadow-xl overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Mountain className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className="w-4 h-4 text-white/70" />
                  <span className="text-white/70 text-sm font-medium">SAVE THE DATE</span>
                </div>
                <h3 className="text-xl font-bold text-white">June 27th Hike</h3>
                <p className="text-white/80 text-sm">Community wellness hike — Details coming soon!</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Follow on Instagram */}
        <a 
          href="https://instagram.com/calvinbuttsms"
          target="_blank" 
          rel="noopener noreferrer"
          className="block"
        >
          <Card className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 border-0 shadow-xl overflow-hidden hover:scale-[1.02] transition-transform">
            <CardContent className="p-4 flex items-center justify-center gap-3">
              <Instagram className="w-6 h-6 text-white" />
              <span className="text-white font-bold text-lg">Follow @calvinbuttsms</span>
            </CardContent>
          </Card>
        </a>

        {/* Partner Credits */}
        <div className="pt-4">
          <p className="text-white/40 text-xs text-center mb-3 uppercase tracking-wider">In Partnership With</p>
          <div className="grid grid-cols-2 gap-3">
            <Card className="bg-white/5 border border-white/10">
              <CardContent className="p-3 text-center">
                <p className="text-white font-semibold text-sm">Simply Fresh Creations</p>
                <p className="text-white/50 text-xs">Fresh Food & Açaí</p>
              </CardContent>
            </Card>
            <Card className="bg-white/5 border border-white/10">
              <CardContent className="p-3 text-center">
                <p className="text-white font-semibold text-sm">Better Bodies ATL</p>
                <p className="text-white/50 text-xs">Wellness & Massage</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-6 text-center">
          <div className="flex items-center justify-center gap-2 text-white/40 text-sm">
            <Heart className="w-4 h-4 text-red-500" />
            <span>Made with love for Metro Atlanta youth</span>
          </div>
          <p className="text-white/30 text-xs mt-2">
            © 2025 Calvin D. Butts Inc. • 501(c)(3) Nonprofit
          </p>
        </div>
      </div>
    </div>
  );
}

export default LinkHub;
